import specJson from '@stable/contracts/openapi.json';

/**
 * Runtime access to the generated OpenAPI contract. The admin builds forms, filters and actions
 * from it, so every create/update DTO and query parameter in the API is reachable without hand-written forms.
 */
export interface JsonSchema {
  type?: string;
  format?: string;
  enum?: Array<string | number>;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  items?: JsonSchema;
  $ref?: string;
  allOf?: JsonSchema[];
  oneOf?: JsonSchema[];
  description?: string;
  example?: unknown;
  default?: unknown;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  pattern?: string;
  nullable?: boolean;
}

export interface Parameter {
  name: string;
  in: 'query' | 'path' | 'header';
  required?: boolean;
  description?: string;
  schema?: JsonSchema;
}

interface Operation {
  operationId?: string;
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: Parameter[];
  requestBody?: { content?: { 'application/json'?: { schema?: JsonSchema } } };
}

type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';

interface Spec {
  paths: Record<string, Partial<Record<Method, Operation>>>;
  components: { schemas: Record<string, JsonSchema> };
}

const spec = specJson as unknown as Spec;

/** Resolve $ref / allOf into one plain schema (depth-limited). */
export function deref(schema: JsonSchema | undefined, depth = 0): JsonSchema {
  if (!schema || depth > 8) return {};
  if (schema.$ref) {
    const name = schema.$ref.split('/').pop()!;
    return deref(spec.components.schemas[name], depth + 1);
  }
  if (schema.allOf?.length) {
    return schema.allOf.reduce<JsonSchema>((acc, part) => {
      const p = deref(part, depth + 1);
      return { ...acc, ...p, properties: { ...(acc.properties ?? {}), ...(p.properties ?? {}) }, required: [...(acc.required ?? []), ...(p.required ?? [])] };
    }, {});
  }
  return schema;
}

export function schemaName(schema: JsonSchema | undefined): string | null {
  return schema?.$ref?.split('/').pop() ?? null;
}

const full = (path: string) => (path.startsWith('/v1/') ? path : `/v1/${path.replace(/^\//, '')}`);

export function operation(method: Method, path: string): Operation | undefined {
  return spec.paths[full(path)]?.[method];
}

export function hasOperation(method: Method, path: string): boolean {
  return Boolean(operation(method, path));
}

/** JSON schema of the request body, or null when the operation takes none. */
export function requestSchema(method: Method, path: string): JsonSchema | null {
  const raw = operation(method, path)?.requestBody?.content?.['application/json']?.schema;
  if (!raw) return null;
  const s = deref(raw);
  return s.properties && Object.keys(s.properties).length ? s : null;
}

export function queryParams(path: string, method: Method = 'get'): Parameter[] {
  return (operation(method, path)?.parameters ?? []).filter((p) => p.in === 'query');
}

/** Whether a list endpoint uses cursor pagination. */
export function isPaginated(path: string): boolean {
  return queryParams(path).some((p) => p.name === 'cursor');
}

export function idempotent(method: Method, path: string): boolean {
  return (operation(method, path)?.parameters ?? []).some((p) => p.in === 'header' && p.name.toLowerCase() === 'idempotency-key');
}

/** Some DELETE routes archive instead of removing (the record stays for history); the operation id says which. */
export function deleteIsArchive(path: string): boolean {
  return /_(archive|deactivate)$/i.test(operation('delete', path)?.operationId ?? '');
}

export function describe(method: Method, path: string): string | undefined {
  const op = operation(method, path);
  return op?.summary || op?.description;
}

export interface ActionOperation {
  method: Method;
  path: string;
  verb: string;
  schema: JsonSchema | null;
  idempotent: boolean;
  description?: string;
}

/**
 * Item-level actions under `<base>/{id}/…` (e.g. `/admin/bookings/{id}/check-in`), excluding sub-collections
 * that a page renders itself.
 */
export function itemActions(basePath: string, exclude: string[] = []): ActionOperation[] {
  const base = full(basePath);
  const out: ActionOperation[] = [];
  for (const [p, ops] of Object.entries(spec.paths)) {
    const m = p.match(new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/\\{id\\}/([a-z0-9-]+)$`));
    if (!m || exclude.includes(m[1])) continue;
    for (const method of ['post', 'put', 'patch'] as Method[]) {
      if (!ops[method]) continue;
      out.push({ method, path: p, verb: m[1], schema: requestSchema(method, p), idempotent: idempotent(method, p), description: describe(method, p) });
    }
  }
  return out;
}

/** All admin/platform operations, for the API explorer and coverage checks. */
export function allOperations(prefixes = ['/v1/admin', '/v1/platform']): Array<{ method: Method; path: string; tag: string; description?: string }> {
  const out: Array<{ method: Method; path: string; tag: string; description?: string }> = [];
  for (const [p, ops] of Object.entries(spec.paths)) {
    if (!prefixes.some((x) => p.startsWith(x))) continue;
    for (const [method, op] of Object.entries(ops) as Array<[Method, Operation]>) out.push({ method, path: p, tag: op.tags?.[0] ?? '', description: op.summary || op.description });
  }
  return out;
}

export { spec };
